<template>
  <div class="bb-contact-visas">
    <transition name="fade" appear>
      <loader-fly v-if="loaderRun"></loader-fly>
    </transition>
    <div class="bb-contact-visas-table" ref="bbContactsVisas">
      <div class="bb-contact-visas-table-row bb-contact-visas-table-row_header"
           :class="{'bb-contact-visas-table-row_header_sticky': sticky}"
           :style="columnsGrid">
        <div class="bb-contact-visas-table-row-cell bb-contact-visas-table-row-cell_title"
             :class="{
             'bb-contact-visas-table-row-cell_file': column.type === 'file'
             }"
             v-for="column in columns"
             :key="column.id">
          {{column.name}}
        </div>
      </div>
      <div class="bb-contact-visas-table-row"
           :class="{
            'bb-contact-visas-table-row_editable': editableRow.id === row.id,
            'bb-contact-visas-table-row_dynamic': isDynamic && (row.height > 55 || currentHeightRow > 55)
           }"
           @mouseleave="hoverRow = false, deleteConfirmIndex = false"
           @mouseenter="hoverRow = row.id"
           :style="columnsGrid"
           v-for="row in rows"
           :key="row.id"
      >
        <div class="bb-contact-visas-table-row-cell"
             :class="{
               'bb-contact-visas-table-row-cell_capitalize': column.capitalize,
               'bb-contact-visas-table-row-cell_editable': editableRow.id === row.id,
               'bb-contact-visas-table-row-cell_file': column.type === 'file'
             }"
             v-for="column in columns"
             :key="column.id"
        >
          <div :key="isKey" v-if="column.type === 'string'">
            <ContactCompanyVisasTableCell
              :row="row"
              :column="column"
              :editableRow="editableRow"
              :currentHeightRow.sync="currentHeightRow"
              @cancelEditable="cancelEditable"
              @input="changeInput"
            />
          </div>
          <div
            :key="isKey"
            v-if="column.type === 'link'"
          >
            <ContactCompanyVisasTableLink
              v-if="(!editableRow.id || editableRow.id !== row.id) && row[column.prop]"
              :link="row[column.prop]"
              :isCopied="copiedItem === row[column.prop]"
              @copy="copyLink(row[column.prop])"
            />
            <ContactCompanyVisasTableCell
              v-show="editableRow.id && editableRow.id === row.id"
              :row="row"
              :column="column"
              :editableRow="editableRow"
              :currentHeightRow.sync="currentHeightRow"
              @cancelEditable="cancelEditable"
              @input="changeInput"
            />
          </div>
          <template v-else-if="column.type === 'select'">
            <BBSelect class="bb-contact-visas-table-row-cell__select"
                      :view-mode="editableRow.id !== row.id"
                      :label="column.label || 'name'"
                      @keyup.enter="saveRow"
                      @keyup.esc="cancelEditable"
                      :placeholder="column.placeholder"
                      :searchable="column.searchable"
                      :options="column.options"
                      :clearable="column.clearable"
                      :value="editableRow.id === row.id ? getVal(editableRow, column) : getVal(row, column)"
                      :item="{row, column}"
                      @change="setSelect"/>
          </template>
          <template v-else-if="column.type === 'selectWithImage'">
            <BBSelectImage class="bb-contact-visas-table-row-cell__select"
                           :view-mode="editableRow.id !== row.id"
                           :label="column.label || 'name'"
                           @keyup.enter="saveRow"
                           @keyup.esc="cancelEditable"
                           :placeholder="column.placeholder"
                           :searchable="column.searchable"
                           :options="column.options"
                           :clearable="column.clearable"
                           :value="editableRow.id === row.id ? getVal(editableRow, column) : getVal(row, column)"
                           :item="{row, column}"
                           @change="setSelect"/>
          </template>
          <template v-else-if="column.type === 'countries'">
            <div class="bb-client-form-body-item__input bb-contact-visas-table-row-cell-countries"
                 v-if="editableRow.id === row.id">
              <v-select label="name"
                        :options="column.options"
                        :clearable="true"
                        :value="getCountry(row, column)"
                        @search="searchCountries($event, column)"
                        @input="setCountry($event, row, column)">
                <template #selected-option="{ name, id }">
                  <div class="bb-contact-visas-table-row-cell-countries-item
                              bb-contact-visas-table-row-cell-countries-item_small
                              bb-contact-visas-table-row-cell-countries_selected">
                    <CountryFlag :country-id="id"/>
                    <span class="bb-contact-visas-table-row-cell-countries-item__name">{{name}}</span>
                  </div>
                </template>
                <template #option="{ name, id }">
                  <div class="bb-contact-visas-table-row-cell-countries-item">
                    <CountryFlag :country-id="id"/>
                    <span class="bb-contact-visas-table-row-cell-countries-item__name">{{name}}</span>
                  </div>
                </template>
              </v-select>
            </div>
            <div class="bb-contact-visas-table-row-cell-countries-item" v-else-if="getCountry(row, column)">
              <CountryFlag :country-id="getCountryProp('id', row, column)"/>
              {{getCountryProp('name', row, column)}}
            </div>
          </template>
          <template v-else-if="column.type === 'date'">
            <DataPicker class="bb-contact-visas-table-row-cell__date-picker"
                        v-if="editableRow.id === row.id && !isRangeDate(row, column)"
                        @setDate="setDate"
                        :form="true"
                        :disabled="getDisabledDate(row, column)"
                        :no-past="column.noPastDate"
                        :obj="{row, column}"
                        :default-date="getDate(row, column)"/>
            <DataPicker class="bb-contact-visas-table-row-cell__date-picker"
                        v-else-if="editableRow.id === row.id && isRangeDate(row, column)"
                        @setDate="setDate"
                        :form="true"
                        :no-past="column.noPastDate"
                        :range="'range'"
                        :obj="{row, column}"
                        :default-date="getRange(row, column)"/>
            <div v-else>{{formatDate(row, column)}}</div>
          </template>
          <template v-if="column.type === 'file'">
            <div
              v-if="isShowUpload(row, column) && !isProgress(row, column)"
              class="bb-contact-visas-table-row-cell-upload"
            >
              <i class="icon-upload bb-icon bb-icon_accent bb-contact-visas-table-row-cell__file"/>
              <input class="bb-contact-visas-table-row-cell-upload__input"
                     :ref="'bbFileInput' + row.id"
                     type="file"
                     @change="setFile($event, row, column)"
                     title="">
            </div>
            <template
              v-if="isProgress(row, column)">
              <div class="bb-contact-visas-table-row-cell-upload-loader">
                <div class="bb-contact-visas-table-row-cell-upload-loader__border">
                  <span class="bb-contact-visas-table-row-cell-upload-loader__progress"/>
                </div>
              </div>
            </template>
            <div class="bb-contact-visas-table-row-cell-upload_view"
                 v-if="displayViewFile(row, column) && !isProgress(row, column) && !isShowUpload(row, column)">
              <i class="icon-eye bb-icon bb-icon_accent bb-contact-visas-table-row-cell__file"
                 @click.stop="clickDownloadFile(row, column)"/>
              <transition name="bounce">
                <Popup class="bb-contact-visas-table-row-cell__popup-file"
                       @change="setPopupValue($event, row, column)"
                       :radio="false"
                       title=""
                       v-if="showControlFile === row.id && activePropFile === column.prop"
                       :options="filesControl"
                       @close="closePopupImageActions"/>
              </transition>
              <transition name="bounce">
                <span class="bb-contact-visas-table-row-cell-upload__error"
                      v-if="editableRow.id === row.id && errorFile">
                {{errorFile}}
              </span>
              </transition>
            </div>
          </template>
          <template v-if="column.type === 'rating'">
            <ContactCompanyRating
              :row="row"
              :column="column"
              :editableRow="editableRow"
              @setRating="setRating"
            />
          </template>
        </div>
        <div class="bb-contact-visas-table-row-cell bb-contact-visas-table-row-cell_right">
          <i
            class="icon-cross bb-contact-visas-table-row-cell__cancel"
            :class="[
              isProgress(row) && 'bb-contact-visas-table-row-cell__icon_disabled'
            ]"
            :disabled="editableRow.uploadFile && !editableRow.uploadFile.path"
            @click="cancelEditable"
            v-if="editableRow.id === row.id"/>
          <i
            v-if="editableRow.id === row.id"
            class="icon-save  bb-contact-visas-table-row-cell__save"
            :class="[
               isProgress(row) && 'bb-contact-visas-table-row-cell__icon_disabled'
            ]"
            :disabled="editableRow.uploadFile && !editableRow.uploadFile.length"
            @click="saveRow"
          />
          <div v-if="editableRow.id !== row.id">
            <transition name="bounce">
              <Toolbar v-if="hoverRow === row.id"
                       :index="hoverRow"
                       :item="row"
                       :noDeleteReasons="false"
                       :delete-index="deleteConfirmIndex"
                       @deleteItem="deleteRow(row)"
                       @openEdit="setEditableRow"
                       @deleteOpen="deleteConfirmIndex = row.id"
                       @deleteClose=" deleteConfirmIndex = false"/>
            </transition>
          </div>
        </div>
      </div>
    </div>
    <DoubleDelete
      v-if="openDoubleDelete"
      @close="openDoubleDelete = false"
      @delete="onDelete"
    />
  </div>
</template>

<script>
import Toolbar from '@/components/Main/Toolbar/Toolbar'
import BBSelect from '@/components/Main/Inputs/BBSelect'
import BBSelectImage from '@/components/Main/Inputs/BBSelectImage'
import DataPicker from '@/components/Main/DataPicker/DataPicker'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import preventBackOnHorizontalScroll from '@/mixin/preventBackOnHorizontalScroll'
import moment from 'moment'
import {cloneDeep, find, findIndex, get, orderBy, set} from 'lodash'
import UploadFileApi from '@/api/UploadFile/api'
import setFileData from '@/mixin/setFileData'
import Popup from '@/components/Main/Popup/Popup'
import vSelect from 'vue-select'
import CountryFlag from '@/components/Main/CountryFlag/CountryFlag'
import countriesList from '@/constants/countriesList'
import {FOUNDER} from '@/constants/roles'
import b64toBlob from 'b64-to-blob'
import {saveAs} from 'file-saver'
import LoaderFly from '@/components/Main/Loaders/LoaderFly'
import DoubleDelete from '@/components/Main/DoubleDelete/DoubleDelete'
import {LinkTwoIcon} from '@/constants/icon'
import ContactCompanyVisasTableCell from "./ContactCompanyVisasTableCell.vue";
import ContactCompanyVisasTableLink from "./ContactCompanyVisasTableLink.vue";
import ContactCompanyRating from "./ContactCompanyRating.vue";

export default {
  name: 'ContactHistoryVendorTable',
  props: ['columns', 'rows', 'sticky', 'checkChangeDates', 'isKey', 'copiedItem'],
  components: {
    ContactCompanyVisasTableCell,
    DoubleDelete,
    VuePerfectScrollbar,
    DataPicker,
    BBSelect,
    BBSelectImage,
    Toolbar,
    Popup,
    vSelect,
    CountryFlag,
    LoaderFly,
    ContactCompanyVisasTableLink,
    ContactCompanyRating,
  },
  mixins: [preventBackOnHorizontalScroll, setFileData],
  data () {
    return {
      errorFile: false,
      editableRow: {},
      hoverRow: '',
      deleteConfirmIndex: false,
      filesControl: [],
      showControlFile: false,
      linkIcon: LinkTwoIcon(),
      countriesList: countriesList,
      crypt: true,
      files: {},
      loaderRun: false,
      openDoubleDelete: false,
      currentRow: {},
      currentHeightRow: 0,
      activePropFile: '',
      loadingPropFile: [],
    }
  },
  computed: {
    isDynamic () {
      const isDynamic = this.columns.find((el) => el.isDynamic)
      return !!isDynamic
    },
    isFounder () {
      const userInfo = this.$store.getters.userInfo || {}
      return userInfo.role === FOUNDER.id
    },
    columnsGrid () {
      const columns = this.columns || []
      const repeat = columns.map(item => item.width || 'minmax(70px, 1fr)').join(' ')
      return {
        gridTemplateColumns: `${repeat} minmax(50px, 91px)`
      }
    }
  },
  methods: {
    closePopupImageActions() {
      this.showControlFile = false;
      this.activePropFile = '';
    },
    copyLink(link) {
      this.$emit('copy', link);
    },
    isShowUpload (row, column) {
      const propUploadFile = column.prop || 'uploadFile';
      if (!Object.keys(this.editableRow).length) {
        const uploadFile = row[propUploadFile]
        return uploadFile && column.isArray && !!uploadFile.length && !uploadFile[0].path;
      }
      const { id } = this.editableRow
      const uploadFile = this.editableRow[propUploadFile] || []
      if (id !== row.id) return false
      const isUpload = column.isArray ? !uploadFile.length : !uploadFile
      return id === row.id && isUpload
    },
    isProgress (row, column = {}) {
      const { id: currentId} = row;
      const propUploadFile = column.prop || 'uploadFile';

      const { id } = this.editableRow;

      return id === currentId && this.loadingPropFile.includes(propUploadFile);
    },
    setDefaultFileControls () {
      this.filesControl = [
        {id: 1, name: 'Upload new', func: this.updateFile},
        {id: 2, name: 'Open file', func: this.downloadFile},
        {id: 3, name: 'Remove', func: this.deleteFile}
      ]
    },
    updateFile () {
      this.deleteFile()
      this.$nextTick(() => {
        const inputNode = this.$refs['bbFileInput' + this.editableRow.id] || []
        if (inputNode[0]) inputNode[0].click()
      })
    },
    clickDownloadFile (row, column) {
      const {id} = row;
      const propUploadFile = column.prop || 'uploadFile';

      const uploadFile = row[propUploadFile];

      if (id === this.editableRow.id) {
        this.showControlFile = id
        this.activePropFile = propUploadFile;
      } else {
        const file = Array.isArray(uploadFile) ? uploadFile[0] : uploadFile
        this.downloadFile(file)
      }
    },
    displayViewFile (row, column) {
      const isEditable = this.editableRow.id === row.id
      const propUploadFile = column.prop || 'uploadFile';

      let editableHasFile
      let rowHasFile
      if (Array.isArray(this.editableRow[propUploadFile] || row[propUploadFile])) {
        editableHasFile = this.editableRow[propUploadFile] &&
          this.editableRow[propUploadFile].length && this.editableRow[propUploadFile][0].path;
        rowHasFile = row[propUploadFile] && row[propUploadFile].length && row[propUploadFile][0].path;
      } else {
        editableHasFile = this.editableRow[propUploadFile] && this.editableRow[propUploadFile].path;
        rowHasFile = row[propUploadFile] && row[propUploadFile].path;
      }

      return (isEditable && editableHasFile) || rowHasFile
    },
    setPopupValue (value, row, column) {
      const propUploadFile = column.prop || 'uploadFile';

      if (value && value.func) {
        const isEditable = row.id === this.editableRow.id
        const file = isEditable ? this.editableRow[propUploadFile] : row[propUploadFile]
        value.func(file)
      }
      this.showPopupCompany = false
    },
    deleteFile () {
      const propFile = this.activePropFile || 'uploadFile';
      const uploadFile = this.editableRow[propFile];

      const row = this.rows.find(row => row.id === this.editableRow.id)
      const fileId = Array.isArray(uploadFile) ? uploadFile[0].id : uploadFile.id;
      UploadFileApi.deleteFile(fileId, this.crypt);

      if (Array.isArray(uploadFile)) {
        this.$set(this.editableRow, propFile, []);
        this.$set(row, propFile, []);
      } else {
        this.$delete(this.editableRow, propFile);
        this.$set(row, propFile, null);
      }

      this.showControlFile = false;
      this.activePropFile = '';
    },
    cancelEditable () {
      const index = findIndex(this.rows, {id: 'new'})
      if (index !== -1) {
        this.rows.splice(index, 1)
      }
      this.editableRow = {}
    },
    checkedUpdateDates (row, rows) {
      if (!this.checkChangeDates) return true
      const OldEntry = find(rows, {id: row.id})
      const newDate = get(row, 'visaExpDate', '')
      const oldDate = get(OldEntry, 'visaExpDate', '')
      const newFile = get(row, 'uploadFile.id', '')
      const oldFile = get(OldEntry, 'uploadFile.id', '')
      const estCardEqualDate = moment(newDate).isSame(oldDate)
      const estCardUpdateFile = newFile !== oldFile
      const validate = !oldDate || !newDate
      return validate || estCardEqualDate || estCardUpdateFile
    },
    saveRow () {
      // if (this.editableRow.uploadFile && !this.editableRow.uploadFile.length) return
      const updatesDates = !this.checkedUpdateDates(this.editableRow, this.rows)
      const isNewRow = this.editableRow.id !== 'new'
      if (updatesDates && isNewRow && !this.isFounder) {
        this.errorFile = 'Need to upload a file'
        setTimeout(() => { this.errorFile = '' }, 10000)
        return
      }
      this.errorFile = ''
      this.$emit('save', this.editableRow, this.rows)
      this.setRowHeight()
      this.showControlFile = false
      this.activePropFile = '';
      this.editableRow = {}
    },
    setRowHeight () {
      const { id } = this.editableRow
      setTimeout(() => {
        const element = document.getElementById(`refs-input_${id}`)
        if (!element) return
        const position = element.getBoundingClientRect()
        if (!position) return
        const { height } = position
        this.currentHeightRow = height
      }, 30)
    },
    isRangeDate (row, column) {
      const activeRow = this.editableRow.id === row.id ? this.editableRow : row
      if (!column.range) return
      const val = get(activeRow, column.dateType, {})
      const idContractType = get(val, 'id', '')
      return idContractType === 1
    },
    getDisabledDate (row, column) {
      const isNew = row.id === 'new'
      if (!column.disabled || isNew) return false
      const prop = get(column, 'disabled.prop', '')
      const noEqual = get(column, 'disabled.noEqual', '')
      const valueRow = get(row, prop, '')
      return valueRow !== noEqual && !this.isFounder
    },
    getRange (row, column) {
      if (!column.range) return
      return get(row, column.range, {end: new Date(), start: new Date()})
    },
    getDate (row, column) {
      return get(row, column.prop, new Date()) || new Date()
    },
    getVal (row, column) {
      return get(row, column.prop, '')
    },
    formatDate (row, column) {
      if (this.isRangeDate(row, column)) {
        const date = get(row, column.range, {})
        const dateStart = get(date, 'start', '')
        const dateEnd = get(date, 'end', '')
        const isValidStart = moment(dateStart).isValid()
        const isValidEnd = moment(dateEnd).isValid()
        const formatStart = isValidStart ? moment(dateStart).format('DD-MMM-YY') : ''
        const formatEnd = isValidEnd ? moment(dateEnd).format('DD-MMM-YY') : ''
        return formatStart + ' - ' + formatEnd
      } else {
        const date = get(row, column.prop, '')
        const isValid = moment(date).isValid()
        return isValid ? moment(date).format('DD-MMM-YY') : ''
      }
    },
    setDate (date, {row, column}) {
      if (this.isRangeDate(row, column)) {
        set(this.editableRow, column.range, date)
      } else {
        set(this.editableRow, column.prop, date)
      }
    },
    setSelect (data, {column}) {
      set(this.editableRow, column.prop, data)
    },
    changeInput (e, row, column) {
      const val = e.target.value
      if (column.format === 'number' && typeof val === 'string' && val) {
        const number = val.replace(/[^.\d]/gmi, '')
        const result = isNaN(+number) ? 0 : number
        e.target.value = result
        set(this.editableRow, column.prop, result)
      }
    },
    setRating(value, prop) {
      this.$set(this.editableRow, prop, value)
    },
    setCountry (item, row, column) {
      const countryId = get(item, 'id', '')
      set(this.editableRow, column.prop, countryId)
    },
    getCountry (row, column) {
      const countryId = get(row, column.prop, '')
      return find(this.countriesList, {id: countryId})
    },
    getCountryProp (prop, row, column) {
      return get(this.getCountry(row, column), prop, '')
    },
    searchCountries (word, column) {
      const countriesList = get(column, 'options', [])
      if (!word) {
        const sortedList = orderBy(this.countriesList, ['name'], ['asc'])
        set(column, 'options', sortedList)
      } else {
        const reg = new RegExp('^' + word, 'gmi')
        const sortedList = countriesList.sort((a, b) => reg.test(b.name) - reg.test(a.name))
        set(column, 'options', sortedList)
      }
    },
    setEditableRow (id, item) {
      this.editableRow = cloneDeep(item)
    },
    onDelete () {
      this.$emit('delete', this.currentRow)
      this.openDoubleDelete = false
    },
    deleteRow (row) {
      this.openDoubleDelete = true
      this.currentRow = row
    },
    downloadFile (file) {
      const propFile = this.activePropFile || 'uploadFile';
      const uploadFile = this.editableRow[propFile];

      const fileEdit = Array.isArray(uploadFile) ? uploadFile[0] : uploadFile
      const fileDef = fileEdit || file
      this.files[fileDef.id]
        ? saveAs(this.files[fileDef.id].blob, this.files[fileDef.id].label)
        : this.getFile(fileDef.id)
    },
    getFile (id) {
      this.loaderRun = true
      UploadFileApi.getFile(id, this.crypt)
        .then((resp) => {
          const blob = b64toBlob(resp.data.base64, resp.data.type)
          this.files[resp.data.id] = {id: resp.data.id, blob, label: resp.data.label}
          saveAs(blob, resp.data.label)
          this.loaderRun = false
        })
        .catch((err) => {
          console.error(err)
          this.loaderRun = false
        })
    },
    setFile (e, row, column) {
      const propUploadFile = column.prop || 'uploadFile';
      const file = e.target.files[0];
      e.target.value = ''
      if (Array.isArray(this.editableRow[propUploadFile])) {
        this.editableRow[propUploadFile][0] = file
      } else {
        this.$set(this.editableRow, propUploadFile, file)
      }
      this.uploadFile(file, propUploadFile)
    },
    uploadFile (fileData, propUploadFile) {
      this.loadingPropFile.push(propUploadFile);
      const file = this.setFileData(fileData)
      UploadFileApi.createFile(file, this.crypt)
        .then((resp) => {
          if (Array.isArray(this.editableRow[propUploadFile])) {
            set(this.editableRow, propUploadFile, [resp.data])
          } else {
            this.$set(this.editableRow, propUploadFile, resp.data)
          }
        })
        .catch((err) => {
          delete this.editableRow[propUploadFile]
          console.error(err)
        })
        .finally(() => {
          this.loadingPropFile = this.loadingPropFile.filter(item => item !== propUploadFile);
        })
    }
  },
  watch: {
    rows (newVal) {
      this.editableRow = find(newVal, {id: 'new'}) || {}
    }
  },
  mounted () {
    const container = get(this.$refs, 'bbContactsVisas.$el', '')
    if (container) this.preventBackOnHorizontalScroll(container)
  },
  beforeMount () {
    this.setDefaultFileControls()
  }
}
</script>

<style lang="scss" scoped>
@import "../../../../../../sass/variables";
@import "../../../../../../sass/mixins";
.bb-contact-visas {
  background: $color-white;
  box-shadow: 0 0 20px rgba($color-black, 0.05);
  border-radius: 5px;

  &-table {
    display: grid;

    &-row {
      position: relative;
      display: grid;
      align-items: center;
      width: 100%;
      border-top: 1px solid $color-wild-sand;
      min-height: 52px;
      grid-gap: 5px;
      font-size: 13px;
      padding: 8px 23px;
      transition: 0.3s;

      &_dynamic {
        align-items: flex-start;
      }

      &:before {
        content: '';
        position: absolute;
        top: auto;
        left: -4px;
        width: 0;
        height: 100%;
        background-color: $color-accent;
        border-radius: 4px 0 0 4px;
      }

      &:hover:not(&_header) {
        box-shadow: 0 0 12px rgba($color-black, 0.15);
        border-radius: 5px;

        &:before {
          width: 4px;
        }
      }

      &_header {
        border-top: none;
        padding: 18px 23px;
        cursor: default;

        &_sticky {
          position: sticky;
          top: 0;
          background-color: $color-white;
          z-index: 2;
        }
      }

      &-cell {
        word-break: break-word;
        height: 100%;
        display: flex;
        align-items: center;
        width: 100%;

        div {
          width: 100%;
        }

        &__link {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: $color-wild-sand;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;
          color: $color-denim;
        }

        &_right {
          display: flex;
          justify-content: flex-end;
          align-items: center;

          div {
            width: auto;
          }
        }

        &_editable {
          margin-left: -10px;
          width: calc(100% - 10px);
        }

        &_capitalize {
          text-transform: capitalize;
        }

        &_title {
          font-weight: 600;
          font-size: 12px;
          line-height: 14px;
          color: $color-accent;
        }

        &_file {
          width: 25px;
          margin-left: 0;
        }

        &__select {
          height: 30px;
          font-size: 13px;
        }

        &__date-picker {
          font-size: 13px;
        }

        &__file {
          width: 21px;
          height: 21px;
          font-size: 21px;
        }

        &__popup-file {
          left: 3px;
        }

        &__input {
          height: 30px;
          display: inline-block;
          width: 100%;
          padding: 6px 10px;
          border: 1px solid $color-mercury;
          box-sizing: border-box;
          border-radius: 5px;
          transition: 0.3s;
          resize: vertical;

          &_dynamic {
            resize: none;
            min-height: 30px;
            max-height: 250px;
          }
        }

        &__cancel {
          color: $color-silver-new;
          font-size: 18px;
          border: 2px solid $color-silver-new;
          border-radius: 50%;
          margin-right: 8px;
          cursor: pointer;

          &:hover {
            opacity: 0.8;
            transition: 0.3s;
          }

          &:before {
            margin: 0;
          }
        }

        &__save {
          color: $color-denim;
          font-size: 21px;
          cursor: pointer;

          &:hover {
            color: $color-accent-hover;
            opacity: 0.8;
            transition: 0.3s;
          }

          &:before {
            margin: 0;
          }
        }

        &__icon_disabled {
          opacity: 0.6;
          pointer-events: none;
          cursor: auto;
        }

        &-upload {
          position: relative;
          width: 21px;
          height: 21px;
          transition: 0.3s;

          &_view {
            position: relative;
          }

          &__error {
            position: absolute;
            top: -20px;
            right: 0;
            color: $color-red-orange;
            font-size: 11px;
            line-height: 13px;
            white-space: nowrap;
          }

          &:hover &__icon {
            &:after {
              background-color: $color-accent-hover
            }
          }

          &__input {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 21px;
            opacity: 0;
            padding: 21px 21px 0 0;
            cursor: pointer;
            z-index: 3;
          }

          &-loader {
            display: flex;
            justify-content: center;
            align-items: center;
            width: 21px;
            height: 21px;
            border-radius: 50%;
            background-color: $color-wild-sand;

            &__border {
              border: 1px solid $color-accent;
              box-sizing: border-box;
              border-radius: 16px;
              width: 15px;
              height: 5px;
            }

            &__progress {
              display: block;
              background-color: $color-accent;
              width: 50%;
              height: 100%;
              transition: .3s;
              animation: loader 3s linear infinite;

              @keyframes loader {
                from {
                  width: 0;
                }

                to {
                  width: 100%;
                }
              }
            }
          }
        }

        &-countries {
          height: 32px;

          &-item {
            height: 32px;
            display: grid;
            grid-template-columns: 22px auto;
            grid-gap: 5px;
            align-items: center;

            &_small {
              margin-right: -30px;
            }
            &__name {
              width: 100%;
              min-width: 25px;
              overflow: hidden;
              white-space: nowrap;
              text-overflow: ellipsis;
            }

            &_selected {
              height: 32px;
            }
          }
        }
      }
    }
  }
}

</style>

<style lang="scss">

.bb-contact-visas-table-row_dynamic {
  .bb-loader-select-header {
    padding: 0 8px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dissemination/Sales/CRM/ContactCompany/ContactCompanyVisas/ContactHistoryVendorTable.vue